import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import adaptivePlugin from '@fullcalendar/adaptive';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  takeUntil,
  throwError,
} from 'rxjs';
import { FULLCALENDAR_LICENSE_KEY } from '../../config/fullcalendar.config';
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE_OPTION,
  DateRangeOption,
  computeDateRange,
} from '../../models/billing-history.models';
import { CAPACITY_STATUS_CODES } from '../../models/capacity.models';
import { LookupOption, LookupTypeCodes } from '../../models/lookup.models';
import {
  PracticeAreaDto,
  ResourceDto,
  ResourceListRequest,
  ResourceTagCategory,
  ResourceTagDto,
  ResourceTimeOffEntryDto,
  ResourceTimeOffExportLayout,
  ResourceTimeOffRequest,
} from '../../models/resource.models';
import { AuthService } from '../../services/auth.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { HolidayDto } from '../../models/holiday.models';
import { LookupService } from '../../services/lookup.service';
import { ResourceService } from '../../services/resource.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TimeOffService } from '../../services/time-off.service';
import { PageKey, UserCookieService } from '../../services/user-cookie.service';
// Aliased: the component method below is also called printCalendar, and an
// unqualified call to a shadowed import reads as recursion. Any refactor that
// qualifies member access with `this.` would then turn it into real recursion
// that still compiles.
import { printCalendar as openPrintDialog } from '../../utils/calendar-print.utils';
import { reconcileHiddenColumns } from '../../utils/column-visibility.utils';
import {
  downloadBlob,
  parseFilenameFromContentDisposition,
} from '../../utils/file-download.utils';
import {
  createTimeOffEventClickHandler,
  mapHolidaysToEventInputs,
  mapTimeOffToEvents,
  timeOffEventDidMount,
  timeOffEventMouseEnter,
  timeOffEventMouseLeave,
} from '../../utils/time-off-calendar.utils';
import { AttributeTagChipsComponent } from '../shared/attribute-tag-chips/attribute-tag-chips.component';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { CapacityLegendComponent } from '../shared/capacity-legend/capacity-legend.component';
import { CustomDateRangeDialogComponent } from '../shared/custom-date-range-dialog/custom-date-range-dialog.component';
import { ErrorStateComponent } from '../shared/error-state/error-state.component';
import {
  ExportConfigDialogComponent,
  ExportConfigDialogData,
  ExportConfigDialogResult,
} from '../shared/export-config-dialog/export-config-dialog.component';
import { MembershipChipsComponent } from '../shared/membership-chips/membership-chips.component';
import { SearchableMultiSelectComponent } from '../shared/searchable-multi-select/searchable-multi-select.component';
import {
  TimeOffDetailsDialogComponent,
  TimeOffDetailsDialogData,
  TimeOffDetailsDialogResult,
} from '../shared/time-off-details-dialog/time-off-details-dialog.component';
import { ResourceDetailsComponent } from './resource-details.component';

/**
 * How many events a day cell shows in the month view before collapsing the rest
 * behind "+N more". Named because printing has to lift the collapse and then
 * restore exactly this value afterwards (PBI 6471) — a printout that hides
 * entries is incomplete.
 */
const DEFAULT_DAY_MAX_EVENTS = 5;

// Interface for weekly capacity with date range
interface WeeklyCapacity {
  statusCode: string;
  statusName: string;
  startDate: Date;
  endDate: Date;
}

@Component({
  selector: 'app-resources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    OverlayModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    FullCalendarModule,
    AttributeTagChipsComponent,
    CapacityLegendComponent,
    ErrorStateComponent,
    MembershipChipsComponent,
    SearchableMultiSelectComponent,
    BreadcrumbComponent,
  ],
  templateUrl: './resources.component.html',
  styleUrls: ['./resources.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('void', style({ height: '0', opacity: 0, overflow: 'hidden' })),
      state('*', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      transition('void => *', [animate('250ms ease-out')]),
      transition('* => void', [animate('200ms ease-in')]),
    ]),
  ],
})
export class ResourcesComponent implements OnInit, AfterViewInit, OnDestroy {
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);
  private timeOffService = inject(TimeOffService);
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private userCookieService = inject(UserCookieService);
  private authService = inject(AuthService);
  private snackbarService = inject(SnackbarService);
  private floatingDialogService = inject(FloatingDialogService);
  private featureFlagService = inject(FeatureFlagService);

  // Every view query here is scoped by template ref. An untyped
  // viewChild(MatSort | MatPaginator) matches the first instance anywhere in the
  // template, and the resources and time-off-requests tables live in mutually
  // exclusive `@if (viewMode === ...)` blocks -- so in requests view an untyped
  // query returns the *requests* table's control, which then gets wired to the
  // resources data source.
  readonly paginator = viewChild<MatPaginator>('resourcesPaginator');
  readonly headerSort = viewChild<MatSort>('headerSort');
  readonly requestsPaginator = viewChild<MatPaginator>('requestsPaginator');
  readonly requestsHeaderSort = viewChild<MatSort>('requestsHeaderSort');
  // Scoped by template ref for the same reason as the queries above, and only
  // resolves while the calendar view is selected.
  readonly timeOffCalendar =
    viewChild<FullCalendarComponent>('timeOffCalendar');

  // Subscription management
  private headerSortSubscription?: Subscription;

  // Data properties
  resources: ResourceDto[] = [];
  practiceAreas: PracticeAreaDto[] = [];
  offices: { code: string; description: string }[] = [];
  titles: string[] = [];
  /**
   * Class-year filter options, viewer-scoped and newest first: only years that
   * at least one visible resource actually has (loaded from the API like
   * titles), so every option returns data.
   */
  classYearOptions: string[] = [];
  isLoading = false;
  isSearching = false;
  isExporting = false;
  error: string | null = null;
  isAdvancedSearchMode = false;
  viewMode: 'table' | 'calendar' | 'requests' = 'table';

  // Remembered export layout — seeds the export-config dialog and is updated
  // whenever the user runs an export from it.
  lastExportType: ResourceTimeOffExportLayout = 'grouped';

  // Condensed-row toggle — reduces vertical padding on the resources and
  // time-off-requests tables; persisted in user-cookie settings.
  isCondensed = false;
  readonly exportOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 0,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: 0,
    },
  ];

  // Calendar properties
  calendarTimeOffEntries: ResourceTimeOffEntryDto[] = [];
  /** True once the calendar has fetched entries; until then there is nothing of its to refresh. */
  private calendarLoadedOnce = false;
  /** Raw holidays from the last calendar load, handed to the details dialog to spare it the fetch. */
  calendarHolidays: HolidayDto[] = [];
  /** Stamped on the print-only caption; refreshed as the print starts. */
  printedOn = new Date();
  calendarStatusFilter: string[] = ['APPROVED'];
  private calendarHolidayEvents: EventInput[] = [];
  isCalendarLoading = false;
  private calendarRequest: Subscription | null = null;

  // Requests tab properties
  canApproveTimeOff = false;
  /** Resources.TimeOff.Write: what the details dialog needs to offer day editing. */
  canEditTimeOffDays = false;
  timeOffRequests: ResourceTimeOffEntryDto[] = [];
  isRequestsLoading = false;
  requestsStatusFilter: string[] = ['PENDING'];
  requestsPracticeAreaFilter: string[] = [];
  requestsOfficeFilter: string[] = [];
  requestsSearchQuery = '';
  requestsTimeFrameFilter: string[] = ['CURRENT', 'FUTURE'];
  private requestsSearchDebounceTimer: ReturnType<typeof setTimeout> | null =
    null;
  private requestsRequest: Subscription | null = null;
  requestsDisplayedColumns: string[] = [
    'fullName',
    'typeName',
    'startDate',
    'endDate',
    'daysOff',
    'officeName',
    'practiceAreas',
    'statusName',
    'actions',
  ];

  requestsDataSource = new MatTableDataSource<ResourceTimeOffEntryDto>([]);

  calendarOptions: CalendarOptions = {
    // adaptivePlugin is FullCalendar Premium: it supplies the print-optimised
    // rendering the print action relies on. It also activates the license
    // check, so schedulerLicenseKey has to be set alongside it.
    plugins: [dayGridPlugin, listPlugin, adaptivePlugin],
    schedulerLicenseKey: FULLCALENDAR_LICENSE_KEY,
    initialView: 'dayGridMonth',
    height: '100%',
    dayMaxEvents: DEFAULT_DAY_MAX_EVENTS,
    views: {
      dayGridWeek: { dayMaxEvents: false },
      dayGridDay: { dayMaxEvents: false },
    },
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
    },
    events: [],
    eventOrder: 'order,start,-duration,allDay,title',
    eventClick: createTimeOffEventClickHandler(() => this.dialog),
    eventDidMount: timeOffEventDidMount,
    eventMouseEnter: timeOffEventMouseEnter,
    eventMouseLeave: timeOffEventMouseLeave,
  };

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;

  // Table configuration. Default column order for the resources table.
  // `openInDialog` is appended dynamically when the `comparison-pop-ups`
  // feature flag is enabled (see ngOnInit) and is always pinned last.
  private readonly DEFAULT_COLUMN_ORDER: string[] = [
    'capacity',
    'billableHours',
    'nonBillableHours',
    'proBonoHours',
    'businessDevelopmentHours',
    'absenceHours',
    'firmHours',
    'totalHours',
    // Sits with the hour columns rather than at the end of the list: it is an
    // hours figure, and the Columns menu reads in this order.
    'expectedPeriodHours',
    'fullName',
    'title',
    'classYear',
    'seniority',
    'office',
    'practiceAreas',
    'skills',
    'jurisdictions',
    'languages',
    'interests',
    'subPracticeAreas',
  ];

  // Which columns each schema version knew about, so reconciliation can tell
  // "the user unhid this column" from "this column did not exist yet" — both of
  // which look like an absence in the saved hiddenColumns list (see
  // reconcileHiddenColumns). Persisting the version number rather than the
  // column list itself keeps the shared ~4 KB user-settings cookie small (every
  // page's preferences live in that one cookie, and an over-limit write is
  // silently dropped by the browser).
  //
  // Bump COLUMN_SCHEMA_VERSION and add an entry here whenever columns are added.
  // Version 1 doubles as the floor: a cookie written before the version marker
  // existed, or carrying a version this build does not know, resolves to it, so
  // any column added later stays hidden by default rather than appearing
  // unbidden for a returning user.
  private readonly COLUMN_SCHEMA_VERSION = 4;
  private readonly COLUMN_SCHEMA_FLOOR_VERSION = 1;
  private readonly COLUMN_SCHEMA_HISTORY: { [version: number]: string[] } = {
    // v1 — pre-#6802, before the five profile tag columns existed.
    1: [
      'capacity',
      'billableHours',
      'nonBillableHours',
      'proBonoHours',
      'businessDevelopmentHours',
      'absenceHours',
      'totalHours',
      'fullName',
      'title',
      'classYear',
      'seniority',
      'office',
      'practiceAreas',
    ],
    // v2 — adds skills, jurisdictions, languages, interests, subPracticeAreas.
    //
    // Spelled out rather than pointing at DEFAULT_COLUMN_ORDER, which it used to. That was a live
    // reference: adding a column to the default order also added it here, so this history would
    // claim v2 already knew about a column introduced later. reconcileHiddenColumns would then
    // treat it as "seen and deliberately shown" for every returning v2 user and the column would
    // appear unbidden - the exact failure the version marker exists to prevent. Past versions are
    // history and must be frozen.
    2: [
      'capacity',
      'billableHours',
      'nonBillableHours',
      'proBonoHours',
      'businessDevelopmentHours',
      'absenceHours',
      'totalHours',
      'fullName',
      'title',
      'classYear',
      'seniority',
      'office',
      'practiceAreas',
      'skills',
      'jurisdictions',
      'languages',
      'interests',
      'subPracticeAreas',
    ],
    // v3 — adds expectedPeriodHours. Frozen like v1 and v2 now that it is history.
    3: [
      'capacity',
      'billableHours',
      'nonBillableHours',
      'proBonoHours',
      'businessDevelopmentHours',
      'absenceHours',
      'totalHours',
      'expectedPeriodHours',
      'fullName',
      'title',
      'classYear',
      'seniority',
      'office',
      'practiceAreas',
      'skills',
      'jurisdictions',
      'languages',
      'interests',
      'subPracticeAreas',
    ],
    // v4 — adds firmHours, the sixth billing bucket.
    4: this.DEFAULT_COLUMN_ORDER,
  };

  // Labels for the Columns menu. Every column can be reordered; `fullName`
  // cannot be hidden so rows always keep their identity (see
  // isColumnHideable), and `openInDialog` is controlled by a feature flag
  // instead.
  private readonly COLUMN_LABELS: { [key: string]: string } = {
    capacity: 'Capacity',
    billableHours: 'Billable Hours',
    nonBillableHours: 'Non-Billable Hours',
    proBonoHours: 'Pro Bono Hours',
    businessDevelopmentHours: 'Business Development Hours',
    absenceHours: 'Absence Hours',
    firmHours: 'Firm Hours',
    totalHours: 'Total Hours',
    fullName: 'Name',
    title: 'Title',
    classYear: 'Class Year',
    seniority: 'Seniority',
    office: 'Office',
    practiceAreas: 'Practice Areas',
    skills: 'Skills',
    jurisdictions: 'Jurisdictions',
    languages: 'Languages',
    interests: 'Interests',
    subPracticeAreas: 'Sub-Practice Areas',
    expectedPeriodHours: 'Expected Period Hours',
  };

  // Hour-type columns hidden until the user opts in via the Columns menu —
  // only Billable Hours shows by default. The five profile tag columns are
  // likewise opt-in (TASK 6802), and Expected Period Hours is off by default
  // per the PBI's tech note for AC 4(a).
  private readonly DEFAULT_HIDDEN_COLUMNS: string[] = [
    'nonBillableHours',
    'proBonoHours',
    'businessDevelopmentHours',
    'absenceHours',
    'firmHours',
    'totalHours',
    'skills',
    'jurisdictions',
    'languages',
    'interests',
    'subPracticeAreas',
    'expectedPeriodHours',
  ];

  // Column order and visibility (Columns menu). Both are persisted in the
  // user-settings cookie; a saved preference replaces the defaults wholesale.
  columnOrder: string[] = [...this.DEFAULT_COLUMN_ORDER];
  hiddenColumns: string[] = [...this.DEFAULT_HIDDEN_COLUMNS];
  showColumnsMenu = false;
  private showOpenInDialogColumn = false;

  displayedColumns: string[] = this.columnOrder.filter(
    (column) => !this.hiddenColumns.includes(column),
  );

  /**
   * The five tag columns: column key -> API tag category (drives the column
   * loop). Header labels are not repeated here -- the template reads them from
   * COLUMN_LABELS via getColumnLabel, the same source the Columns menu uses, so
   * a rename cannot leave the menu and the header disagreeing.
   */
  readonly tagColumns: { key: string; category: ResourceTagCategory }[] = [
    { key: 'skills', category: 'Skill' },
    { key: 'jurisdictions', category: 'Jurisdiction' },
    { key: 'languages', category: 'Language' },
    { key: 'interests', category: 'Interest' },
    { key: 'subPracticeAreas', category: 'SubPractice' },
  ];

  // Per-row cache for getResourceTags so each change-detection pass hands the
  // chip components stable array references (rows are replaced wholesale on
  // reload, so entries fall away with their row objects).
  private readonly tagsByResource = new WeakMap<
    ResourceDto,
    Partial<Record<ResourceTagCategory, ResourceTagDto[]>>
  >();

  /** A resource's tags for one category (memoized per row object). */
  getResourceTags(
    resource: ResourceDto,
    category: ResourceTagCategory,
  ): ResourceTagDto[] {
    let byCategory = this.tagsByResource.get(resource);
    if (!byCategory) {
      byCategory = {};
      this.tagsByResource.set(resource, byCategory);
    }
    return (byCategory[category] ??=
      resource.tags?.filter((tag) => tag.category === category) ?? []);
  }

  dataSource = new MatTableDataSource<ResourceDto>([]);
  private pendingSearchRequest: Subscription | null = null;
  private sortInitialized = false;

  // Capacity legend hover state
  showCapacityLegend = false;
  capacityLegendPosition = { x: 0, y: 0 };
  private capacityLegendTimeout: ReturnType<typeof setTimeout> | null = null;

  // Date range selector properties
  selectedDateRange: DateRangeOption = DEFAULT_DATE_RANGE_OPTION; // Will be loaded from settings
  dateRangeOptions = DATE_RANGE_OPTIONS;
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  private readonly SETTINGS_PAGE_KEY = PageKey.ResourcesDirectory;

  showCapacityLegendHover(event: MouseEvent): void {
    if (this.capacityLegendTimeout) {
      clearTimeout(this.capacityLegendTimeout);
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const cardWidth = Math.min(viewportWidth * 0.95, 1200);

    this.capacityLegendPosition = {
      x: (viewportWidth - cardWidth) / 2,
      y: rect.bottom + 8,
    };

    this.capacityLegendTimeout = setTimeout(() => {
      this.showCapacityLegend = true;
      this.cdr.detectChanges();
    }, 300);
  }

  hideCapacityLegendHover(): void {
    if (this.capacityLegendTimeout) {
      clearTimeout(this.capacityLegendTimeout);
    }
    this.showCapacityLegend = false;
    this.cdr.detectChanges();
  }

  // Filter form
  filterForm: FormGroup;

  // Current request state
  currentRequest: ResourceListRequest = {
    page: 1,
    pageSize: 25,
    sortBy: 'LastName',
    sortDirection: 'asc',
  };

  private destroy$ = new Subject<void>();
  private isClearingFilters = false;

  constructor() {
    // Initialize filter form
    this.filterForm = this.formBuilder.group({
      primarySearch: [''],
      search: [''],
      titles: [[]],
      title: [''],
      classYears: [[]],
      offices: [[]],
      office: [''],
      practiceAreaCodes: [[]],
      practiceAreaCode: [''],
      capacityStatuses: [[]],
      seniorityCodes: [[]],
      skillCodes: [[]],
      jurisdictionCodes: [[]],
      languageCodes: [[]],
      interestCodes: [[]],
      subPracticeAreaCodes: [[]],
    });
  }

  // Capacity filter options (loaded from lookup table)
  capacityFilterOptions: LookupOption[] = [];

  // Seniority filter options (loaded from lookup table)
  seniorityFilterOptions: LookupOption[] = [];

  /** Resource profile attribute filters: form control name -> API tag category. */
  private readonly attributeFilterFields: {
    control: string;
    category: ResourceTagCategory;
  }[] = [
    { control: 'skillCodes', category: 'Skill' },
    { control: 'jurisdictionCodes', category: 'Jurisdiction' },
    { control: 'languageCodes', category: 'Language' },
    { control: 'interestCodes', category: 'Interest' },
    { control: 'subPracticeAreaCodes', category: 'SubPractice' },
  ];

  /**
   * Multi-select filters living inside the advanced-search panel, excluding the tag filters in
   * attributeFilterFields. Kept as one list because these names were previously spelled out
   * individually in restore, persist, the auto-open check and the active-filter count, so adding a
   * filter meant remembering four places and a miss produced a subtly wrong count or a lost
   * selection.
   */
  private readonly advancedSelectionFields = [
    'titles',
    'offices',
    'practiceAreaCodes',
    'seniorityCodes',
    'classYears',
  ];

  /**
   * Every multi-select filter persisted for this page. capacityStatuses is included here but
   * deliberately NOT in advancedSelectionFields: it lives on the basic filter bar, so restoring it
   * must not spring the advanced-search panel open.
   */
  private readonly directorySelectionFields = [
    'capacityStatuses',
    ...this.advancedSelectionFields,
  ];

  /** True once cookie-restored tag codes have been checked against the loaded
   *  filter options (see pruneRestoredAttributeFilters). Needed because tag
   *  options refetch on tagsChanged$, and a second prune must not clear a
   *  selection the user just made; class years have no refetch path, so their
   *  prune needs no such flag. */
  private attributeFiltersPruned = false;

  /**
   * Option lists whose arrival can prune a cookie-restored selection. The initial loadResources()
   * goes out before any of them land, so a prune means the search has to be re-issued — and left
   * uncoordinated, each list re-issued it for itself, costing up to three search-with-billing
   * requests on one page load with two abandoned after the server had already begun the unpaged
   * employee load and its capacity/billing/tag fan-out.
   *
   * A debounce cannot merge these: the responses land on separate HTTP completions that may be far
   * apart. So each source reports in when it settles, and the re-issue happens once, after the
   * last one. See notePruneSourceSettled.
   */
  private readonly pruneSources = ['tags', 'classYears', 'titles'] as const;
  private readonly settledPruneSources = new Set<string>();
  private pruneNeedsReload = false;

  /** Settle time before refetching filter options after a tag edit, so adding
   *  several tags in a row costs one request. */
  private readonly TAG_CHANGE_DEBOUNCE_TIME = 500;

  // Attribute filter options keyed by form control name (loaded from lookup table)
  attributeFilterOptions: Record<string, LookupOption[]> = {
    skillCodes: [],
    jurisdictionCodes: [],
    languageCodes: [],
    interestCodes: [],
    subPracticeAreaCodes: [],
  };

  ngOnInit(): void {
    this.configureClientSideSorting();

    // Load saved timespan preference from settings
    this.selectedDateRange = this.userCookieService.getBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
    );

    // Restore last-chosen export layout for the combo button.
    const savedExportType = this.userCookieService.getFilterValue(
      this.SETTINGS_PAGE_KEY,
      'exportType',
    );
    if (savedExportType === 'flattened' || savedExportType === 'grouped') {
      this.lastExportType = savedExportType;
    }

    // Restore condensed-row preference for the resources/requests tables.
    this.isCondensed =
      this.userCookieService.getFilterValue(
        this.SETTINGS_PAGE_KEY,
        'condensedRows',
      ) === 'true';

    // Load saved custom dates if custom range is selected
    if (this.isCustomRange()) {
      const savedDates = this.userCookieService.getCustomDateRange(
        this.SETTINGS_PAGE_KEY,
      );
      if (savedDates) {
        this.customStartDate = savedDates.startDate;
        this.customEndDate = savedDates.endDate;
      }
    }

    // Restore saved time off request filters
    const savedOffices = this.userCookieService.getFilterSelection(
      PageKey.TimeOffRequests,
      'offices',
    );
    if (savedOffices) {
      this.requestsOfficeFilter = savedOffices;
    }
    const savedPracticeAreas = this.userCookieService.getFilterSelection(
      PageKey.TimeOffRequests,
      'practiceAreas',
    );
    if (savedPracticeAreas) {
      this.requestsPracticeAreaFilter = savedPracticeAreas;
    }
    const savedStatus = this.userCookieService.getFilterSelection(
      PageKey.TimeOffRequests,
      'status',
    );
    if (savedStatus && savedStatus.length > 0) {
      this.requestsStatusFilter = savedStatus;
    }
    const savedTimeFrames = this.userCookieService.getFilterSelection(
      PageKey.TimeOffRequests,
      'timeFrames',
    );
    if (savedTimeFrames && savedTimeFrames.length > 0) {
      this.requestsTimeFrameFilter = savedTimeFrames;
    }

    // Restore view mode from query param (e.g. ?view=requests) so reloads
    // preserve the active tab.
    const requestedView = this.route.snapshot.queryParamMap.get('view');
    if (
      requestedView === 'table' ||
      requestedView === 'calendar' ||
      requestedView === 'requests'
    ) {
      this.viewMode = requestedView;
    }

    // Subscribe to role changes so the time-off requests tab appears
    // even when roles load asynchronously after a page refresh.
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.canApproveTimeOff = this.authService.canApproveTimeOff();
        this.canEditTimeOffDays = this.authService.canSetResourceTimeOff();
        // Only enforce the permission fallback once roles have actually been
        // loaded. The BehaviorSubject seeds an empty [] before the API call
        // resolves, which would otherwise incorrectly force us off the
        // requests tab on a page refresh.
        if (
          roles &&
          roles.length > 0 &&
          this.viewMode === 'requests' &&
          !this.canApproveTimeOff
        ) {
          this.viewMode = 'table';
          this.updateViewModeQueryParam('table');
        }
      });

    // Restore column visibility and order preferences, dropping any stale
    // keys that no longer match a known column. Columns added since the
    // user's preference was written are forced back to their default-hidden
    // state (see reconcileHiddenColumns) via the persisted schema version.
    const savedHiddenColumns = this.userCookieService.getFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'hiddenColumns',
    );
    // Early builds of this feature persisted the full column list under
    // `knownColumns`; honour such a cookie once, then drop the key below so it
    // stops consuming cookie space.
    const legacyKnownColumnList = this.userCookieService.getFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'knownColumns',
    );
    const savedSchemaVersion = this.getSavedColumnSchemaVersion();
    const knownColumns =
      legacyKnownColumnList ??
      this.knownColumnsForSchemaVersion(savedSchemaVersion);
    if (savedHiddenColumns) {
      this.hiddenColumns = reconcileHiddenColumns(
        savedHiddenColumns,
        knownColumns,
        {
          allColumns: this.DEFAULT_COLUMN_ORDER,
          defaultHidden: this.DEFAULT_HIDDEN_COLUMNS,
          isHideable: (column) => this.isColumnHideable(column),
        },
      );
      // Persist the reconciled set when it differs, so the next visit does
      // not depend on re-running the reconciliation against the same cookie.
      if (
        this.hiddenColumns.length !== savedHiddenColumns.length ||
        this.hiddenColumns.some((c) => !savedHiddenColumns.includes(c))
      ) {
        this.userCookieService.setFilterSelection(
          this.SETTINGS_PAGE_KEY,
          'hiddenColumns',
          [...this.hiddenColumns],
        );
      }
    }
    // Record the column set this config has now seen, so columns added later
    // can default to hidden for returning users too.
    if (savedSchemaVersion !== this.COLUMN_SCHEMA_VERSION) {
      this.userCookieService.setFilterValue(
        this.SETTINGS_PAGE_KEY,
        'columnsSchemaVersion',
        String(this.COLUMN_SCHEMA_VERSION),
      );
    }
    if (legacyKnownColumnList) {
      this.userCookieService.removeFilterSelection(
        this.SETTINGS_PAGE_KEY,
        'knownColumns',
      );
    }
    const savedColumnOrder = this.userCookieService.getFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'columnOrder',
    );
    if (savedColumnOrder) {
      this.columnOrder = this.reconcileColumnOrder(savedColumnOrder);
    }
    this.updateDisplayedColumns();

    // Toggle the "Open in floating dialog" column on the comparison-pop-ups flag.
    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.showOpenInDialogColumn = enabled;
        this.updateDisplayedColumns();
      });

    this.loadCapacityStatusOptions();
    this.loadSeniorityOptions();
    this.loadAttributeFilterOptions();
    // A tag added or removed anywhere (e.g. the attributes card in the floating
    // resource dialog opened over this list) changes the distinct-tag set the
    // filter dropdowns are built from, and this component is not destroyed in
    // between. Debounced so a burst of rapid edits costs one refetch.
    this.resourceService.tagsChanged$
      .pipe(
        debounceTime(this.TAG_CHANGE_DEBOUNCE_TIME),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.loadAttributeFilterOptions());
    this.loadUserPracticeAreas();
    this.loadUniqueOffices();
    this.loadUniqueTitles();
    this.loadClassYearOptions();
    this.restoreDirectoryFilters();
    this.loadResources();
    this.setupFilterSubscriptions();
    // Initialize custom dates with the loaded range
    this.initializeCustomDates();

    // Load tab-specific data when restoring a non-default view on refresh.
    if (this.viewMode === 'calendar') {
      this.loadCalendarTimeOff();
    } else if (this.viewMode === 'requests') {
      this.loadTimeOffRequests();
    }
  }

  /**
   * The column set a saved preference of the given schema version had seen.
   * A missing version (a cookie predating the marker) or one this build does not
   * recognise both resolve to the floor version, which is the conservative
   * choice: every column added since then is treated as unseen and so stays
   * hidden by default.
   */
  private knownColumnsForSchemaVersion(version: number | null): string[] {
    return (
      (version !== null ? this.COLUMN_SCHEMA_HISTORY[version] : undefined) ??
      this.COLUMN_SCHEMA_HISTORY[this.COLUMN_SCHEMA_FLOOR_VERSION]
    );
  }

  /**
   * The column-schema version recorded in the cookie, or null when it is absent
   * or unparseable.
   */
  private getSavedColumnSchemaVersion(): number | null {
    const saved = this.userCookieService.getFilterValue(
      this.SETTINGS_PAGE_KEY,
      'columnsSchemaVersion',
    );
    if (!saved) {
      return null;
    }
    const parsed = Number(saved);
    return Number.isInteger(parsed) ? parsed : null;
  }

  private updateViewModeQueryParam(
    mode: 'table' | 'calendar' | 'requests',
  ): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: mode },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngAfterViewInit(): void {
    // Wire client-side sorting + pagination (and the header/body sort sync) once the
    // table has rendered.
    setTimeout(() => {
      this.wireDataSourceSortAndPaginator();
      this.setupSortSubscriptions();
    }, 200);
  }

  /**
   * Teaches the MatTableDataSource how to read each sortable column's value so the
   * resource list can sort client-side, including by every billing-hours bucket and
   * by seniority rank.
   */
  private configureClientSideSorting(): void {
    this.dataSource.sortingDataAccessor = (
      resource: ResourceDto,
      columnId: string,
    ): string | number => {
      switch (columnId) {
        case 'fullName':
          return (
            resource.fullName ||
            `${resource.lastName ?? ''} ${resource.firstName ?? ''}`
          )
            .trim()
            .toLowerCase();
        case 'title':
          return (resource.title || '').toLowerCase();
        case 'office':
          return (resource.office || '').toLowerCase();
        case 'classYear':
          // A string, not a number — blanks sort first ascending, and real years collate the same
          // as they would numerically because they are all the same width.
          return resource.classYear || '';
        case 'seniority':
          // Use the server-provided rank so ordering stays in sync with the
          // seniority lookup; unclassified resources sort last.
          return resource.senioritySortOrder ?? Number.MAX_SAFE_INTEGER;
        // Every billing bucket sorts the same way: figures first, dashes last, both directions.
        case 'billableHours':
          return this.billedHoursSortValue(resource.billableHours);
        case 'nonBillableHours':
          return this.billedHoursSortValue(resource.nonBillableHours);
        case 'proBonoHours':
          return this.billedHoursSortValue(resource.proBonoHours);
        case 'businessDevelopmentHours':
          return this.billedHoursSortValue(resource.businessDevelopmentHours);
        case 'absenceHours':
          return this.billedHoursSortValue(resource.absenceHours);
        case 'firmHours':
          return this.billedHoursSortValue(resource.firmHours);
        case 'totalHours':
          return this.billedHoursSortValue(resource.totalHours);
        case 'expectedPeriodHours': {
          // Same lookup the cell renders, so the sort can never disagree with what is on screen.
          // Memoised because MatTableDataSource calls this inside the comparator - twice per
          // comparison - and this case walks date ranges rather than reading a field like its
          // siblings above.
          const hours = this.currentPeriodExpectedHoursForSort(resource);
          // Only null counts as a dash here - deliberately NOT zero, unlike the billing columns
          // above. This column renders 0 as `0 hrs`, because a resource who entered 0 has reported
          // something and the API keeps that distinct from having reported nothing. Treating it as
          // a dash would file everyone who has not answered in among the ones who answered "zero".
          //
          // The two definitions of "shows a dash" differing between neighbouring cases is not an
          // oversight; see billedHoursSortValue for why the billing buckets cannot make the same
          // distinction.
          if (hours === null || hours === undefined) {
            return this.dashSortsLastSentinel();
          }
          return hours;
        }
        default:
          return '';
      }
    };
  }

  /**
   * The value that sorts a row whose cell shows no number to the bottom, whichever way the column
   * is sorted.
   *
   * The direction is needed because MatTableDataSource only reverses whatever the accessor returns:
   * a fixed sentinel would put the blank-looking rows last one way and first the other. Sorting an
   * hours column high-to-low asks "who has the most", and a page of dashes above the real figures
   * does not answer it.
   *
   * Read off `dataSource.sort`, NOT the `headerSort()` viewChild: `sortData` compares using
   * whichever MatSort the data source holds, and the two are briefly different objects while the
   * table is destroyed and re-created around a search. Taking the direction from the instance that
   * does the comparing makes them agree by construction.
   */
  private dashSortsLastSentinel(): number {
    return this.dataSource.sort?.direction === 'desc'
      ? Number.NEGATIVE_INFINITY
      : Number.POSITIVE_INFINITY;
  }

  /**
   * Sort value for one of the billing-hours buckets.
   *
   * Zero counts as "no number shown" here, which is the opposite of how the Expected Period Hours
   * column treats it. Two reasons, and both have to hold for this to stay correct:
   *
   * 1. `formatHours` renders 0 as an em dash, so on screen a zero is indistinguishable from a blank.
   * 2. The server writes 0 into these fields when the external API returns no billing summary for a
   *    resource, so a 0 may not even mean "billed nothing" - it can mean "no summary came back".
   *
   * Sorting such a row among the real figures would drop blank-looking rows into the middle of the
   * numbers. If `formatHours` ever starts rendering 0 as `0 hrs`, this must change with it - there
   * are tests on that formatter pinning the contract.
   */
  private billedHoursSortValue(value: number | null | undefined): number {
    return value === null || value === undefined || value === 0
      ? this.dashSortsLastSentinel()
      : value;
  }

  /**
   * Current-period expected hours for one row, memoised for the sort comparator.
   *
   * MatTableDataSource calls the sorting accessor from inside its comparator rather than projecting
   * once, so a single header click on this column would otherwise re-walk every resource's date
   * ranges O(n log n) times. The Directory is unpaged - the list endpoint returns every match - so
   * that cost scales with the whole result set, not with a page.
   *
   * A WeakMap keyed by the row needs no invalidation: a reload replaces the row objects wholesale,
   * so stale entries become unreachable on their own.
   */
  private readonly expectedHoursSortCache = new WeakMap<
    ResourceDto,
    number | null
  >();

  private currentPeriodExpectedHoursForSort(
    resource: ResourceDto,
  ): number | null {
    const cached = this.expectedHoursSortCache.get(resource);
    // `has`, not a truthiness check: null is a real cached answer here, meaning "reported nothing".
    if (cached !== undefined || this.expectedHoursSortCache.has(resource)) {
      return cached ?? null;
    }

    const value = this.getCurrentPeriodExpectedHours(resource);
    this.expectedHoursSortCache.set(resource, value);
    return value;
  }

  /**
   * Binds the (re-created) header MatSort and paginator to the data source for
   * client-side sorting/pagination. Idempotent and safe to call after every load,
   * since the table is destroyed/re-created when the result set goes empty or a
   * search toggles its visibility.
   */
  private wireDataSourceSortAndPaginator(): void {
    const sort = this.headerSort();
    const paginator = this.paginator();
    if (sort && this.dataSource.sort !== sort) {
      this.dataSource.sort = sort;
    }
    if (paginator && this.dataSource.paginator !== paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  private setupSortSubscriptions(): void {
    if (this.headerSortSubscription) {
      this.headerSortSubscription.unsubscribe();
    }

    // `#headerSort` is the resources table's only MatSort, and the
    // MatTableDataSource performs the actual client-side sort. One subscription
    // is enough to reset paging and track the active sort for hasFiltersChanged.
    const headerSort = this.headerSort();
    if (headerSort) {
      this.headerSortSubscription = headerSort.sortChange
        .pipe(takeUntil(this.destroy$))
        .subscribe((sort: Sort) => this.onSortChange(sort));
    }
  }

  ngOnDestroy(): void {
    // Clean up sort subscriptions
    if (this.headerSortSubscription) {
      this.headerSortSubscription.unsubscribe();
    }
    if (this.calendarRequest) {
      this.calendarRequest.unsubscribe();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFilterSubscriptions(): void {
    const SEARCH_DEBOUNCE_TIME = 500;

    // Primary search subscription
    this.filterForm
      .get('primarySearch')
      ?.valueChanges.pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        if (!this.isClearingFilters) {
          this.handleSearchValueChange(value);
        }
      });

    // Advanced search multi-select filters.
    //
    // Debounced for the same reason the text fields are. These are `mat-select multiple` with no
    // apply button, so each ticked option emits its own valueChanges — selecting k values without
    // this fires k searches against /resources/search-with-billing, the heaviest endpoint on the
    // page, plus k runs of persistDirectoryFilters (a full parse+stringify+write of the shared
    // cookie per filter). Cancelling the previous XHR does not stop the server-side work or the
    // outbound billing calls it has already started, so the waste is real, not just client-side.
    [
      ...this.advancedSelectionFields,
      ...this.attributeFilterFields.map((f) => f.control),
    ].forEach((field) => {
      this.filterForm
        .get(field)
        ?.valueChanges.pipe(
          debounceTime(SEARCH_DEBOUNCE_TIME),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          if (!this.isClearingFilters && this.isAdvancedSearchMode) {
            this.handleAdvancedSearchChange();
          } else if (!this.isClearingFilters) {
            // Also trigger for legacy mode
            this.onFilterChange();
          }
        });
    });

    // Legacy search field with debounce (keep for backward compatibility)
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!this.isClearingFilters) {
          this.onFilterChange();
        }
      });

    // Capacity status filter - triggers server-side filtering via API
    this.filterForm
      .get('capacityStatuses')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isClearingFilters) {
          this.applyCapacityFilter();
        }
      });
  }

  private loadCapacityStatusOptions(): void {
    this.lookupService
      .getLookupOptions(LookupTypeCodes.RESOURCE_CAPACITY_STATUS)
      .subscribe({
        next: (options) => {
          this.capacityFilterOptions = options;
        },
        error: (error) => {
          console.error('Error loading capacity status options:', error);
          // Continue without options - non-critical for table functionality
        },
      });
  }

  private loadSeniorityOptions(): void {
    this.lookupService.getLookupOptions(LookupTypeCodes.SENIORITY).subscribe({
      next: (options) => {
        this.seniorityFilterOptions = options;
      },
      error: (error) => {
        console.error('Error loading seniority options:', error);
        // Continue without options - non-critical for table functionality
      },
    });
  }

  private loadAttributeFilterOptions(): void {
    // Filter options come from the visibility-scoped distinct-tags endpoint, so
    // the dropdowns only offer values actually assigned to visible resources.
    this.resourceService
      .getVisibleTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tags) => {
          for (const field of this.attributeFilterFields) {
            this.attributeFilterOptions[field.control] = tags
              .filter((tag) => tag.category === field.category)
              .map((tag) => ({ value: tag.code, label: tag.name }));
          }
          this.pruneRestoredAttributeFilters();
        },
        error: (error) => {
          console.error('Error loading attribute filter options:', error);
          // Continue without options - non-critical for table functionality.
          // No prune on failure: better to keep a possibly-stale selection than
          // wipe a valid one because the options request failed.
          this.notePruneSourceSettled('tags', false);
        },
      });
  }

  /**
   * Records that one prune-capable option list has settled, and re-issues the resource search once
   * every source has — but only if at least one of them actually dropped a value.
   *
   * Each source must report from its success *and* its failure path, or the set never fills and
   * the re-issue never happens. Repeat reports are ignored, which matters because the tag options
   * refetch on tagsChanged$: a later failure there must not be counted a second time and trigger a
   * spurious search. If a request never completes at all the prune simply never applies and the
   * stale selection survives the session — the same outcome a failed options request already had
   * before this coordination existed.
   */
  private notePruneSourceSettled(
    source: (typeof this.pruneSources)[number],
    pruned: boolean,
  ): void {
    if (this.settledPruneSources.has(source)) {
      return;
    }
    this.settledPruneSources.add(source);
    this.pruneNeedsReload = this.pruneNeedsReload || pruned;

    if (
      this.settledPruneSources.size < this.pruneSources.length ||
      !this.pruneNeedsReload
    ) {
      return;
    }

    // The initial request already went out with the stale values; ask for a search so the
    // correction refreshes in place instead of repainting the skeleton rows over already-rendered
    // results. loadResources downgrades this back to a load if the initial one is still in
    // flight, so nothing has rendered to refresh in place yet.
    this.loadResources(true);
  }

  /**
   * Drops cookie-restored tag codes the loaded filter options no longer offer.
   *
   * Unlike seniority or practice areas, tag values are user-generated: one can be
   * retired, or simply stop being returned once the last visible resource holding
   * it loses the tag. A selected code with no matching option renders nowhere in
   * the multi-select — the field reads "1 selected", the list returns zero rows,
   * and there is no checkbox to un-check — so the stale code is pruned (and
   * re-persisted) instead.
   *
   * Runs only for the initial cookie restore; later refreshes (see the
   * tagsChanged$ subscription) must not clear a selection the user just made.
   */
  private pruneRestoredAttributeFilters(): void {
    if (this.attributeFiltersPruned) {
      return;
    }
    this.attributeFiltersPruned = true;

    let pruned = false;
    for (const field of this.attributeFilterFields) {
      const available = new Set(
        (this.attributeFilterOptions[field.control] ?? []).map((o) => o.value),
      );
      pruned = this.pruneRestoredSelection(field.control, available) || pruned;
    }

    this.notePruneSourceSettled('tags', pruned);
  }

  /**
   * Drop cookie-restored values the given option list no longer offers, and
   * re-persist the shrunken selection. A selected value with no matching option
   * renders nowhere in the multi-select — the field reads "1 selected", the grid
   * filters on the invisible value, and there is no checkbox to un-check.
   *
   * Returns whether anything was dropped, so the caller can re-issue the search
   * exactly once across all its controls.
   */
  private pruneRestoredSelection(
    controlName: string,
    available: Set<string>,
  ): boolean {
    const control = this.filterForm.get(controlName);
    const selected: string[] = control?.value ?? [];
    if (selected.length === 0) {
      return false;
    }

    const kept = selected.filter((value) => available.has(value));
    if (kept.length === selected.length) {
      return false;
    }

    control?.setValue(kept, { emitEvent: false });
    this.userCookieService.setFilterSelection(
      this.SETTINGS_PAGE_KEY,
      controlName,
      kept,
    );
    return true;
  }

  private loadUserPracticeAreas(): void {
    this.resourceService.getUserPracticeAreas().subscribe({
      next: (practiceAreas) => {
        this.practiceAreas = practiceAreas;
      },
      error: (error) => {
        console.error('Error loading practice areas:', error);
        // Continue without practice areas - non-critical for table functionality
      },
    });
  }

  private loadUniqueOffices(): void {
    this.resourceService.getUniqueOffices().subscribe({
      next: (offices) => {
        this.offices = offices;
      },
      error: (error) => {
        console.error('Error loading offices:', error);
        // Continue without offices - non-critical for table functionality
      },
    });
  }

  private loadUniqueTitles(): void {
    // takeUntil matters here more than on the plain options loaders: the next
    // handler can chain a prune that re-issues a whole resource search, which
    // must not fire on a destroyed component.
    this.resourceService
      .getUniqueTitles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (titles) => {
          this.titles = titles;
          this.pruneRestoredTitles();
        },
        error: (error) => {
          console.error('Error loading titles:', error);
          // Continue without titles - non-critical for table functionality.
          // No prune on failure: better to keep a possibly-stale selection than
          // wipe a valid one because the options request failed.
          this.notePruneSourceSettled('titles', false);
        },
      });
  }

  /**
   * Drop cookie-restored titles the loaded option list no longer offers — see
   * pruneRestoredSelection for the pathology this prevents.
   *
   * Titles come from the same viewer-scoped employee cache as class years, so a saved title stops
   * being offered once the last visible resource holding it changes title or leaves the firm,
   * stranding the user with a zero-result filter they cannot un-tick.
   *
   * Skipped on an EMPTY option list for the same reason as class years: the API degrades query
   * failures to an empty 200, so an empty list can mean "the query failed" rather than "no titles
   * exist", and treating it as authoritative would let a transient error wipe a valid selection.
   */
  private pruneRestoredTitles(): void {
    if (this.titles.length === 0) {
      this.notePruneSourceSettled('titles', false);
      return;
    }

    this.notePruneSourceSettled(
      'titles',
      this.pruneRestoredSelection('titles', new Set(this.titles)),
    );
  }

  private loadClassYearOptions(): void {
    // takeUntil matters here more than on the plain options loaders: the next
    // handler can chain a prune that re-issues a whole resource search, which
    // must not fire on a destroyed component.
    this.resourceService
      .getUniqueClassYears()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (classYears) => {
          this.classYearOptions = classYears;
          this.pruneRestoredClassYears();
        },
        error: (error) => {
          console.error('Error loading class years:', error);
          // Continue without class years - non-critical for table functionality.
          // No prune on failure: better to keep a possibly-stale selection than
          // wipe a valid one because the options request failed.
          this.notePruneSourceSettled('classYears', false);
        },
      });
  }

  /**
   * Drop cookie-restored class years that no longer exist in the loaded option
   * list (e.g. the last resource with that year left the firm) — see
   * pruneRestoredSelection for the pathology.
   *
   * Deliberately skipped when the option list is EMPTY: the API degrades query
   * failures to an empty 200 (same swallow policy as titles), so an empty list
   * can mean "the query failed", not "no class years exist". Treating it as
   * authoritative would let a transient server error permanently wipe a valid
   * saved selection; keeping a possibly-stale selection for one session is the
   * safer trade, matching the HTTP-error path above.
   */
  private pruneRestoredClassYears(): void {
    if (this.classYearOptions.length === 0) {
      this.notePruneSourceSettled('classYears', false);
      return;
    }

    this.notePruneSourceSettled(
      'classYears',
      this.pruneRestoredSelection('classYears', new Set(this.classYearOptions)),
    );
  }

  private loadResources(isSearchOperation: boolean = false): void {
    // Cancel any pending request. Unsubscribing means its next/error handlers
    // never run, so it can never clear the flag it set — this call inherits
    // that responsibility. When the request being replaced is an unfinished
    // non-search load, run this one as a load too rather than as a search: the
    // search presentation ("Searching resources...") is meant to sit over
    // already-rendered rows, and nothing has rendered yet. Downgrading also
    // keeps isLoading owned by a live request; leaving it set with no handler
    // to clear it strands the page, since the date-range select is disabled on
    // it and the error and empty states are both gated on !isLoading.
    const replacingUnfinishedLoad =
      this.pendingSearchRequest !== null && this.isLoading;
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
      this.pendingSearchRequest = null;
    }
    const runAsSearch = isSearchOperation && !replacingUnfinishedLoad;

    this.updateRequest();

    if (runAsSearch) {
      this.isSearching = true;
      // Don't set isLoading for search operations
    } else {
      this.isLoading = true;
      this.isSearching = false;
      // Show skeleton data for all non-search operations (initial load, filter changes, pagination)
      this.showSkeletonData();
    }
    this.error = null;

    const { startDate, endDate } = this.calculateBillingDateRange();

    this.pendingSearchRequest = this.resourceService
      .getResourcesWithBilling(this.currentRequest, startDate, endDate)
      .subscribe({
        next: (items: ResourceDto[]) => {
          this.resources = items;
          this.totalCount = items.length;
          this.updateDataSource(items);

          setTimeout(() => {
            this.wireDataSourceSortAndPaginator();
            this.paginator()?.firstPage();
            this.cdr.detectChanges();
          });

          // Only set isLoading to false if this was not a search operation
          if (!runAsSearch) {
            this.isLoading = false;
          }
          this.isSearching = false;
          this.pendingSearchRequest = null;
        },
        error: (error) => {
          console.error('Error loading resources:', error);
          this.error = 'Failed to load resources. Please try again.';

          // Only set isLoading to false if this was not a search operation
          if (!runAsSearch) {
            this.isLoading = false;
            this.resources = [];
            this.updateDataSource([]);
            this.totalCount = 0;
          }
          this.isSearching = false;
          this.pendingSearchRequest = null;
        },
      });
  }

  private showSkeletonData(): void {
    // Create skeleton data that matches the page size
    const skeletonData = this.createSkeletonData(this.pageSize);
    this.dataSource.data = skeletonData;
  }

  private createSkeletonData(count: number): ResourceDto[] {
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        id: -(index + 1), // Negative IDs to identify skeleton rows
        firstName: '',
        lastName: '',
        fullName: '',
        employeeId: '',
        email: '',
        phoneNumber: '',
        title: '',
        classYear: '',
        office: '',
        seniority: '',
        seniorityCode: '',
        practiceAreas: [],
        assignments: [],
        resourceCapacities: undefined,
        billableHours: undefined,
        nonBillableHours: undefined,
        proBonoHours: undefined,
        businessDevelopmentHours: undefined,
        absenceHours: undefined,
        firmHours: undefined,
        totalHours: undefined,
      }));
  }

  private updateDataSource(newData: ResourceDto[]): void {
    // Update the data source without causing a visual jump
    this.dataSource.data = newData;
  }

  private calculateBillingDateRange(): { startDate: string; endDate: string } {
    return computeDateRange(this.selectedDateRange, {
      startDate: this.customStartDate,
      endDate: this.customEndDate,
    });
  }

  onFilterChange(): void {
    // Reset to first page when filtering. The active sort persists on its own:
    // the MatSort retains its state and the MatTableDataSource re-applies it to the
    // reloaded data, so there's nothing to carry on the request.
    this.pageIndex = 0;

    // Persist current filter selections so they survive reloads
    this.persistDirectoryFilters();

    this.updateRequest();

    // Check if this is a search operation or other filter
    const searchValue = this.filterForm.get('search')?.value?.trim();

    // Treat any filter change as a search operation to trigger immediate search
    const hasActiveFilters =
      searchValue ||
      this.advancedSelectionFields.some((field) => this.hasSelection(field)) ||
      this.hasAnyAttributeFilter();

    if (hasActiveFilters) {
      // For any filter changes, use the search behavior
      this.loadResources(true);
    } else {
      // For clearing all filters, don't show skeleton data
      this.loadResources();
    }

    // Also reload calendar if in calendar view
    if (this.viewMode === 'calendar') {
      this.loadCalendarTimeOff();
    }
  }

  onPageChange(event: PageEvent): void {
    // Paging itself is handled client-side by MatTableDataSource.paginator. We still track
    // pageIndex/pageSize because they're read elsewhere: the paginator's [pageIndex]/[pageSize]
    // bindings (keeping its display in sync) and skeleton sizing (createSkeletonData(pageSize)).
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  onSortChange(_sort: Sort): void {
    // Sorting is performed client-side by the MatTableDataSource (wired to the header
    // MatSort); we only need to jump back to the first page so the top of the newly
    // sorted list is visible. hasFiltersChanged reads the live MatSort directly.
    this.paginator()?.firstPage();
  }

  private updateRequest(): void {
    // Get values directly from form controls instead of form.value
    const primarySearchValue = this.filterForm.get('primarySearch')?.value;
    const searchValue = this.filterForm.get('search')?.value;
    const titlesValue = this.filterForm.get('titles')?.value;
    const titleValue = this.filterForm.get('title')?.value;
    const classYearsValue = this.filterForm.get('classYears')?.value;
    const officesValue = this.filterForm.get('offices')?.value;
    const officeValue = this.filterForm.get('office')?.value;
    const practiceAreaCodesValue =
      this.filterForm.get('practiceAreaCodes')?.value;
    const practiceAreaCodeValue =
      this.filterForm.get('practiceAreaCode')?.value;
    const capacityStatusesValue =
      this.filterForm.get('capacityStatuses')?.value;
    const seniorityCodesValue = this.filterForm.get('seniorityCodes')?.value;

    // Determine the search term to use
    let finalSearchTerm = '';

    if (primarySearchValue?.trim()) {
      // Use primary search
      finalSearchTerm = primarySearchValue.trim();
    } else if (searchValue?.trim()) {
      // Fallback to legacy search field
      finalSearchTerm = searchValue.trim();
    }

    this.currentRequest = {
      page: this.pageIndex + 1, // Material paginator is 0-based, API is 1-based
      pageSize: this.pageSize,
      sortBy: this.currentRequest.sortBy || 'LastName',
      sortDirection: this.currentRequest.sortDirection || 'asc',
      search: finalSearchTerm || undefined,
      titles: titlesValue?.length > 0 ? titlesValue : undefined,
      title: titleValue?.trim() || undefined,
      classYears: classYearsValue?.length > 0 ? classYearsValue : undefined,
      offices: officesValue?.length > 0 ? officesValue : undefined,
      office: officeValue?.trim() || undefined,
      practiceAreaCodes:
        practiceAreaCodesValue?.length > 0 ? practiceAreaCodesValue : undefined,
      practiceAreaCode: practiceAreaCodeValue?.trim() || undefined,
      capacityStatusCodes:
        capacityStatusesValue?.length > 0 ? capacityStatusesValue : undefined,
      seniorityCodes:
        seniorityCodesValue?.length > 0 ? seniorityCodesValue : undefined,
      skillCodes: this.getAttributeFilterValue('skillCodes'),
      jurisdictionCodes: this.getAttributeFilterValue('jurisdictionCodes'),
      languageCodes: this.getAttributeFilterValue('languageCodes'),
      interestCodes: this.getAttributeFilterValue('interestCodes'),
      subPracticeAreaCodes: this.getAttributeFilterValue(
        'subPracticeAreaCodes',
      ),
    };
  }

  /** The control's selected codes, or undefined when nothing is selected. */
  private getAttributeFilterValue(control: string): string[] | undefined {
    const value = this.filterForm.get(control)?.value;
    return value?.length > 0 ? value : undefined;
  }

  /** True when any of the five attribute filters has a selection. */
  private hasAnyAttributeFilter(): boolean {
    return this.attributeFilterFields.some(
      (field) => !!this.getAttributeFilterValue(field.control),
    );
  }

  resetFilters(): void {
    this.filterForm.reset(
      {
        primarySearch: '',
        search: '',
        titles: [],
        title: '',
        classYears: [],
        offices: [],
        office: '',
        practiceAreaCodes: [],
        practiceAreaCode: '',
        capacityStatuses: [],
        seniorityCodes: [],
        skillCodes: [],
        jurisdictionCodes: [],
        languageCodes: [],
        interestCodes: [],
        subPracticeAreaCodes: [],
      },
      { emitEvent: false },
    );

    // Reset sorting to default (name ascending). Use the 'fullName' column key so
    // the MatTableDataSource's client-side sort and the header arrow both reset.
    this.currentRequest.sortBy = 'LastName';
    this.currentRequest.sortDirection = 'asc';
    const headerSort = this.headerSort();
    if (headerSort) {
      headerSort.active = 'fullName';
      headerSort.direction = 'asc';
    }

    this.pageIndex = 0;
    this.updateRequest();
    // Don't show skeleton data when clearing filters
    this.loadResources();

    // Persist cleared filter selections
    this.persistDirectoryFilters();

    // Also reload calendar if in calendar view
    if (this.viewMode === 'calendar') {
      this.loadCalendarTimeOff();
    }
  }

  hasFiltersChanged(): boolean {
    const primarySearch = this.filterForm.get('primarySearch')?.value?.trim();
    const search = this.filterForm.get('search')?.value?.trim();

    // Check if sorting differs from the default (name ascending). Read the live
    // MatSort, since sorting is client-side and no sort state is tracked on the request.
    const activeSort = this.headerSort();
    const sortingChanged =
      !!activeSort?.active &&
      (activeSort.active !== 'fullName' || activeSort.direction !== 'asc');

    return !!(
      primarySearch ||
      search ||
      // capacityStatuses counts here, unlike in the advanced-filter count below.
      this.directorySelectionFields.some((field) => this.hasSelection(field)) ||
      this.hasAnyAttributeFilter() ||
      sortingChanged
    );
  }

  retry(): void {
    this.loadResources();
  }

  getPracticeAreaNames(practiceAreas: PracticeAreaDto[]): string {
    if (!practiceAreas || practiceAreas.length === 0) {
      return '—';
    }
    return practiceAreas.map((pa) => pa.description).join(', ');
  }

  /**
   * True when a multi-select filter control holds at least one value. Every caller used to inline
   * its own control read plus a `value && value.length > 0` check, so each new array filter had to
   * be added in four places and a miss produced a wrong filter count or a skipped search.
   */
  private hasSelection(fieldName: string): boolean {
    const value = this.filterForm.get(fieldName)?.value;
    return Array.isArray(value) && value.length > 0;
  }

  // Optimized method that returns the count string or empty string
  getSelectedCountDisplay(fieldName: string): string {
    if (!this.hasSelection(fieldName)) {
      return '';
    }
    return `(${this.filterForm.get(fieldName)!.value.length})`;
  }

  // Advanced search methods
  private handleSearchValueChange(value: string): void {
    const primarySearch = value?.trim();
    const hasAdvancedSearch = this.hasActiveAdvancedFilters();

    if (!primarySearch && !hasAdvancedSearch) {
      // No search criteria - show all resources
      this.onFilterChange();
    } else {
      // Has search criteria - perform search
      this.onFilterChange();
    }
  }

  private handleAdvancedSearchChange(): void {
    this.onFilterChange();
  }

  toggleAdvancedSearch(): void {
    this.isAdvancedSearchMode = !this.isAdvancedSearchMode;

    // Do not clear advanced search fields when minimizing
    // Do not trigger search when toggling advanced search mode
    // Advanced filters are preserved and only cleared when:
    // - Reloading the page
    // - Clicking "Clear All"
  }

  // Method to count enabled advanced filters
  getEnabledAdvancedFiltersCount(): number {
    let count = this.advancedSelectionFields.filter((field) =>
      this.hasSelection(field),
    ).length;

    for (const field of this.attributeFilterFields) {
      if (this.getAttributeFilterValue(field.control)) count++;
    }

    return count;
  }

  // Method to check if any advanced filters are active
  hasActiveAdvancedFilters(): boolean {
    return this.getEnabledAdvancedFiltersCount() > 0;
  }

  // Method to get advanced search button text
  getAdvancedSearchButtonText(): string {
    const count = this.getEnabledAdvancedFiltersCount();
    if (this.isAdvancedSearchMode) {
      return 'Hide Advanced';
    } else if (count > 0) {
      return `Advanced Search (${count})`;
    } else {
      return 'Advanced Search';
    }
  }

  // Method to check if advanced search button should be highlighted
  isAdvancedSearchButtonActive(): boolean {
    return this.isAdvancedSearchMode || this.hasActiveAdvancedFilters();
  }

  onEnterKeySearch(): void {
    // Force immediate search on Enter key
    this.onFilterChange();
  }

  /**
   * Navigate to resource details page
   */
  navigateToResource(resource: ResourceDto, event?: MouseEvent): void {
    // Don't navigate if this is a skeleton row
    if (resource.id < 0) {
      return;
    }

    // Check if the click was on capacity elements - if so, don't navigate
    if (event) {
      const target = event.target as HTMLElement;
      const isCapacityElement =
        target.closest('.capacity-dots') ||
        target.closest('.status-dot-svg') ||
        target.closest('td.mat-column-capacity');

      if (isCapacityElement) {
        event.stopPropagation();
        return;
      }
    }

    this.router.navigate(['/resources', resource.employeeId]);
  }

  navigateToResourceById(employeeId: string): void {
    this.router.navigate(['/resources', employeeId]);
  }

  openInFloatingDialog(resource: ResourceDto, event: MouseEvent): void {
    event.stopPropagation();
    this.floatingDialogService.open(
      ResourceDetailsComponent,
      resource.fullName,
      {
        employeeId: resource.employeeId,
      },
    );
  }

  /**
   * Get practice areas text
   */
  getPracticeAreasText(practiceAreas: PracticeAreaDto[]): string {
    if (!practiceAreas || practiceAreas.length === 0) {
      return '';
    }
    return practiceAreas.map((pa) => pa.description).join(' • ');
  }

  /**
   * Format hours for display - show "—" if value is 0 or undefined
   */
  formatHours(value: number | undefined): string {
    if (value === undefined || value === null || value === 0 || value === 0.0) {
      return '—';
    }
    // Format to 1 decimal place and add "hrs" suffix
    return parseFloat(value.toFixed(1)).toString() + ' hrs';
  }

  /**
   * Formats reported expected hours for the directory.
   *
   * Deliberately not formatHours: that renders 0 as an em dash, which is right for billed totals -
   * nobody "billed zero", they simply have no hours - but wrong here. A resource who entered 0 has
   * reported something, and the API keeps that distinct from having entered nothing. Collapsing the
   * two would show an empty cell for a period the resource has actually filled in.
   */
  formatExpectedPeriodHours(value: number | null): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return parseFloat(value.toFixed(1)).toString() + ' hrs';
  }

  /**
   * Get weekly capacity statuses for the previous week, current week, and next week
   * Uses calendar-based week calculation to ensure correct alignment
   */
  getWeeklyCapacities(resource: ResourceDto): WeeklyCapacity[] {
    // Return empty array for skeleton rows
    if (resource.id < 0) {
      return [];
    }

    // Calculate the 3 target weeks based on today's date
    const today = new Date();
    const targetWeeks = this.calculateTargetWeeks(today);

    // If no capacity data, return unknown statuses for all 3 weeks
    if (
      !resource.resourceCapacities ||
      !resource.resourceCapacities.resourceCapacities ||
      resource.resourceCapacities.resourceCapacities.length === 0
    ) {
      return targetWeeks.map((week) => ({
        statusCode: CAPACITY_STATUS_CODES.UNKNOWN,
        statusName: 'Unknown',
        startDate: week.start,
        endDate: week.end,
      }));
    }

    // For each target week, find the matching capacity data
    return targetWeeks.map((targetWeek) => {
      const result = this.findCapacityForWeek(
        resource.resourceCapacities!.resourceCapacities,
        targetWeek.start,
      );
      return {
        statusCode: result.statusCode,
        statusName: result.statusName,
        startDate: targetWeek.start,
        endDate: targetWeek.end,
      };
    });
  }

  /**
   * The expected hours this resource reported for the period covering today, or null when none.
   *
   * A period TOTAL, matching what the capacity card shows - the API resolves it server-side across
   * the interval's weeks, so it must not be summed or divided again here.
   *
   * The list payload covers a three-week window, so the current period is picked by date rather
   * than by position: a resource on a two-week interval has fewer entries than one on a one-week
   * interval, and indexing would read the wrong period for half the directory.
   */
  getCurrentPeriodExpectedHours(resource: ResourceDto): number | null {
    // Skeleton rows carry no data.
    if (resource.id < 0) {
      return null;
    }

    const intervals = resource.resourceCapacities?.resourceCapacities;
    if (!intervals || intervals.length === 0) {
      return null;
    }

    // Normalised to midnight before comparing. parseApiDate discards the time - the API's interval
    // end is 23:59:59.9999999 on the last day, but it parses to 00:00 - so comparing against a
    // `new Date()` that carries a time makes the last day of every period fail its own range check.
    // Same convention as calculateTargetWeeks, which also compares whole days.
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const covering = intervals.find((interval) => {
      const start = this.parseApiDate(interval.startDate);
      const end = this.parseApiDate(interval.endDate);
      return today >= start && today <= end;
    });

    // ?? not ||, so a reported 0 survives: the API distinguishes "reported zero hours" from
    // "reported nothing", and || would turn the first into the second.
    return covering?.expectedHours ?? null;
  }

  /**
   * Calculate the start (Sunday) and end (Saturday) dates for previous, current, and next weeks
   */
  private calculateTargetWeeks(today: Date): Array<{ start: Date; end: Date }> {
    // Find the Sunday of the current week
    const currentWeekStart = new Date(today);
    const dayOfWeek = currentWeekStart.getDay(); // 0 = Sunday
    currentWeekStart.setDate(currentWeekStart.getDate() - dayOfWeek);
    currentWeekStart.setHours(0, 0, 0, 0);

    const weeks: Array<{ start: Date; end: Date }> = [];

    // Previous week, current week, next week
    for (let weekOffset = -1; weekOffset <= 1; weekOffset++) {
      const weekStart = new Date(currentWeekStart);
      weekStart.setDate(currentWeekStart.getDate() + weekOffset * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weeks.push({ start: weekStart, end: weekEnd });
    }

    return weeks;
  }

  /**
   * Find the capacity status for a specific week from the capacity data
   */
  private findCapacityForWeek(
    capacities: any[],
    weekStart: Date,
  ): { statusCode: string; statusName: string } {
    for (const capacity of capacities) {
      const intervalStart = this.parseApiDate(capacity.startDate);
      const intervalEnd = this.parseApiDate(capacity.endDate);

      // Check if the target week falls within this interval
      if (weekStart >= intervalStart && weekStart <= intervalEnd) {
        // Check if this is a mixed status interval with weekly breakdowns
        const isMixedStatus =
          capacity.capacityStatus?.code === CAPACITY_STATUS_CODES.MIXED;

        if (
          isMixedStatus &&
          capacity.weeklyCapacities &&
          capacity.weeklyCapacities.length > 0
        ) {
          // For mixed status, find the matching weekly capacity
          const weeklyCapacity = capacity.weeklyCapacities.find((wc: any) => {
            const wcStart = this.parseApiDate(wc.startDate);
            const wcEnd = this.parseApiDate(wc.endDate);
            return weekStart >= wcStart && weekStart <= wcEnd;
          });

          if (weeklyCapacity) {
            return {
              statusCode:
                weeklyCapacity.capacityStatus?.code ??
                CAPACITY_STATUS_CODES.UNKNOWN,
              statusName: weeklyCapacity.capacityStatus?.name ?? 'Unknown',
            };
          }
        }

        // Use the interval status (for non-mixed or if no weekly match found)
        return {
          statusCode:
            capacity.capacityStatus?.code ?? CAPACITY_STATUS_CODES.UNKNOWN,
          statusName: capacity.capacityStatus?.name ?? 'Unknown',
        };
      }
    }

    // No matching capacity found
    return {
      statusCode: CAPACITY_STATUS_CODES.UNKNOWN,
      statusName: 'Unknown',
    };
  }

  /**
   * Get CSS class for capacity status code
   */
  getCapacityStatusClass(statusCode: string | undefined): string {
    switch (statusCode) {
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return 'status-dot-below-capacity';
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return 'status-dot-at-capacity';
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return 'status-dot-over-capacity';
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return 'status-dot-not-available';
      default:
        return 'status-dot-unknown';
    }
  }

  /**
   * Filter resources by their current week's capacity status
   * @param resources The resources to filter
   * @param selectedStatuses Array of selected capacity status values ('unknown', 'below', 'at', 'over', 'notAvailable')
   */
  private filterResourcesByCapacity(
    resources: ResourceDto[],
    selectedStatuses: string[],
  ): ResourceDto[] {
    return resources.filter((resource) => {
      // Get the weekly capacities for this resource
      const weeklyCapacities = this.getWeeklyCapacities(resource);

      // Get the current week's status (index 1 is the center/current week)
      const currentWeekCapacity = weeklyCapacities[1];
      const currentStatusCode = currentWeekCapacity?.statusCode;

      // The status code now matches the filter values directly
      return selectedStatuses.includes(
        currentStatusCode || CAPACITY_STATUS_CODES.UNKNOWN,
      );
    });
  }

  /**
   * Restore previously persisted directory filter selections from the cookie.
   * Called before subscriptions are wired so patching the form does not
   * trigger a redundant search.
   */
  private restoreDirectoryFilters(): void {
    const patch: { [key: string]: any } = {};
    for (const field of [
      ...this.directorySelectionFields,
      // Restored tag codes are validated against the loaded filter options once
      // those arrive — see pruneRestoredAttributeFilters.
      ...this.attributeFilterFields.map((f) => f.control),
    ]) {
      const selection = this.userCookieService.getFilterSelection(
        this.SETTINGS_PAGE_KEY,
        field,
      );
      if (selection) patch[field] = selection;
    }

    // The class-year filter used to persist a single value under 'classYear';
    // it is now a multi-select saved as 'classYears'. Migrate the old value into
    // the new selection (pruneRestoredClassYears validates it against the loaded
    // options, so a year with no resources still gets cleaned up), then drop the
    // retired key so it stops occupying space in the ~4 KB cookie every page
    // shares. Deleting without migrating would silently discard the user's saved
    // filter across the upgrade.
    //
    // Only whole four-digit years in 1900-2050 migrate, matching the retired
    // parseClassYear contract. The old key stored the raw text the user typed,
    // and parseClassYear ignored anything outside that contract — so a
    // half-typed "20" never filtered anything before. Carrying it over would
    // turn it into a real selected value: advanced search opens, the API
    // filters on "20", and the grid shows zero resources, self-healing only
    // once a non-empty class-year option list arrives to prune it.
    const legacyClassYear = this.userCookieService.getFilterValue(
      this.SETTINGS_PAGE_KEY,
      'classYear',
    );
    const savedClassYears = patch['classYears'];
    if (
      legacyClassYear &&
      /^\d{4}$/.test(legacyClassYear.trim()) &&
      Number(legacyClassYear.trim()) >= 1900 &&
      Number(legacyClassYear.trim()) <= 2050 &&
      !savedClassYears?.length
    ) {
      patch['classYears'] = [legacyClassYear.trim()];
      this.userCookieService.setFilterSelection(
        this.SETTINGS_PAGE_KEY,
        'classYears',
        patch['classYears'],
      );
    }
    this.userCookieService.removeFilterValue(
      this.SETTINGS_PAGE_KEY,
      'classYear',
    );

    if (Object.keys(patch).length > 0) {
      this.filterForm.patchValue(patch, { emitEvent: false });
      // Auto-open advanced search if any of its filters were restored from cookie.
      // capacityStatuses is excluded on purpose — it lives on the basic filter bar.
      if (
        this.advancedSelectionFields.some(
          (field) => patch[field]?.length > 0,
        ) ||
        this.attributeFilterFields.some(
          (field) => patch[field.control]?.length > 0,
        )
      ) {
        this.isAdvancedSearchMode = true;
      }
      // Sync currentRequest so the initial loadResources call uses the
      // restored filters.
      this.updateRequest();
    }
  }

  /**
   * Persist directory filter selections to the cookie so they survive
   * page reloads.
   */
  private persistDirectoryFilters(): void {
    for (const field of [
      ...this.directorySelectionFields,
      ...this.attributeFilterFields.map((f) => f.control),
    ]) {
      this.userCookieService.setFilterSelection(
        this.SETTINGS_PAGE_KEY,
        field,
        this.filterForm.get(field)?.value ?? [],
      );
    }
  }

  /**
   * Apply capacity filter by making a new API request with capacity status codes
   * Server-side filtering ensures pagination works correctly with the filter
   */
  private applyCapacityFilter(): void {
    // Reset to first page when capacity filter changes
    this.pageIndex = 0;
    const paginator = this.paginator();
    if (paginator) {
      paginator.pageIndex = 0;
    }

    // Persist capacity selection so it survives reloads
    this.persistDirectoryFilters();

    // Reload resources - the filtering happens in loadResources
    this.loadResources();

    // Also reload calendar if in calendar view
    if (this.viewMode === 'calendar') {
      this.loadCalendarTimeOff();
    }
  }

  /**
   * Get tooltip text for weekly capacity
   */
  getCapacityTooltip(weeklyCapacity: WeeklyCapacity): string {
    const statusText = weeklyCapacity.statusName || 'Unknown';
    const startDateStr = this.formatDate(weeklyCapacity.startDate);
    const endDateStr = this.formatDate(weeklyCapacity.endDate);
    return `${statusText}\n${startDateStr} - ${endDateStr}`;
  }

  /**
   * Format date for tooltip display
   */
  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Parse API date strings (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) as local dates
   * to avoid timezone-related day shifts
   */
  private parseApiDate(dateString: string): Date {
    const datePart = dateString.split('T')[0];
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return new Date(dateString);
    }
    return new Date(year, month - 1, day);
  }

  // Date range selector methods
  onDateRangeChange(key: string): void {
    const option = this.dateRangeOptions.find((opt) => opt.key === key);
    if (option) {
      this.selectDateRange(option);
    }
  }

  selectDateRange(option: DateRangeOption): void {
    this.selectedDateRange = option;

    // Save the selected timespan to settings
    this.userCookieService.setBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
      option,
    );

    this.initializeCustomDates();

    if (this.isCustomRange()) {
      this.openCustomDateDialog();
    } else {
      this.loadResources();
    }
  }

  openCustomDateDialog(): void {
    const dialogRef = this.dialog.open(CustomDateRangeDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        startDate: this.customStartDate,
        endDate: this.customEndDate,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.customStartDate = result.startDate;
        this.customEndDate = result.endDate;

        // Save custom dates to cookie
        if (this.customStartDate && this.customEndDate) {
          this.userCookieService.setCustomDateRange(
            this.SETTINGS_PAGE_KEY,
            this.customStartDate,
            this.customEndDate,
          );
        }

        // Reload the full list for the chosen custom window.
        this.loadResources();
        this.cdr.detectChanges();
      }
    });
  }

  isCustomRange(): boolean {
    return this.selectedDateRange.kind === 'custom';
  }

  private initializeCustomDates(): void {
    const today = new Date();

    if (this.isCustomRange()) {
      // For custom range, only set defaults if not already loaded from cookie
      if (!this.customStartDate) {
        this.customStartDate = new Date(today);
        this.customStartDate.setDate(this.customStartDate.getDate() - 30);
      }
      if (!this.customEndDate) {
        this.customEndDate = new Date(today);
      }
    } else {
      // For preset ranges, seed the custom dates from the computed window so the
      // displayed start/end text stays in sync with the selection.
      const { startDate, endDate } = computeDateRange(this.selectedDateRange);
      this.customStartDate = this.parseLocalDate(startDate);
      this.customEndDate = this.parseLocalDate(endDate);
    }
  }

  /** Parse a local `yyyy-MM-dd` string into a Date at local midnight. */
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  getDateRangeStartText(): string {
    const { startDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = startDate.split('-').map(Number);
    const startDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDateObj)}`;
  }

  getDateRangeEndText(): string {
    const { endDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = endDate.split('-').map(Number);
    const endDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(endDateObj)}`;
  }

  private calculateDateRange(): { startDate: string; endDate: string } {
    return computeDateRange(this.selectedDateRange, {
      startDate: this.customStartDate,
      endDate: this.customEndDate,
    });
  }

  // ---- Calendar / Time-Off integration ----

  onViewModeChange(mode: 'table' | 'calendar' | 'requests'): void {
    this.viewMode = mode;
    this.updateViewModeQueryParam(mode);
    if (mode === 'calendar') {
      this.loadCalendarTimeOff();
    } else if (mode === 'requests') {
      this.loadTimeOffRequests();
    } else if (mode === 'table') {
      setTimeout(() => this.wireDataSourceSortAndPaginator());
    }
  }

  loadCalendarTimeOff(): void {
    if (this.calendarRequest) {
      this.calendarRequest.unsubscribe();
    }

    this.isCalendarLoading = true;
    this.updateRequest();

    const request: ResourceTimeOffRequest = {
      ...this.currentRequest,
    };

    this.calendarRequest = forkJoin({
      timeOff: this.resourceService.getFilteredResourcesTimeOff(request),
      holidays: this.timeOffService.getHolidays(),
    }).subscribe({
      next: ({ timeOff, holidays }) => {
        this.calendarTimeOffEntries = timeOff.timeOffEntries;
        this.calendarLoadedOnce = true;
        this.calendarHolidays = holidays;
        this.calendarHolidayEvents = mapHolidaysToEventInputs(holidays);
        this.refilterCalendarEvents();
        this.isCalendarLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading calendar time off:', err);
        this.isCalendarLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /** Opens the export-config dialog, seeded from the page's current selections.
   *  The dialog owns the export's billing window, time-off filters, and layout;
   *  the rest (search, offices, practice areas, …) is inherited from the page. */
  openExportConfig(): void {
    if (this.isExporting) {
      return;
    }

    // Keep currentRequest in sync with the form so the inherited filter count
    // (and the export body) reflect what the user sees.
    if (this.viewMode !== 'requests') {
      this.updateRequest();
    }

    const { statusCodes, timeFrames } = this.exportTimeOffDefaults();
    const isRequestsView = this.viewMode === 'requests';

    const data: ExportConfigDialogData = {
      layout: this.lastExportType,
      dateRangeKey: this.selectedDateRange.key,
      customStartDate: this.customStartDate,
      customEndDate: this.customEndDate,
      statusCodes,
      timeFrames,
      viewLabel: isRequestsView ? 'Time Off Requests' : 'Resources Directory',
      activeFilterCount: isRequestsView
        ? this.activeRequestsFilterCount()
        : this.activeDirectoryFilterCount(),
      // The dialog runs the export itself so it can show progress on its Export
      // button and close only once the download has completed.
      runExport: (config: ExportConfigDialogResult) =>
        this.runExportWithConfig(config),
    };

    this.dialog.open(ExportConfigDialogComponent, {
      width: '460px',
      autoFocus: false,
      disableClose: true,
      data,
    });
  }

  /** Time-off status/time-frame selections to seed the dialog with, prefilled
   *  from whatever the active tab is showing. The list view doesn't filter time
   *  off today, so it defaults to every status and time frame (i.e. all entries). */
  private exportTimeOffDefaults(): {
    statusCodes: string[];
    timeFrames: string[];
  } {
    if (this.viewMode === 'requests') {
      return {
        statusCodes: [...this.requestsStatusFilter],
        timeFrames: [...this.requestsTimeFrameFilter],
      };
    }
    if (this.viewMode === 'calendar') {
      return {
        statusCodes: [...this.calendarStatusFilter],
        timeFrames: ['PAST', 'CURRENT', 'FUTURE'],
      };
    }
    return {
      statusCodes: ['PENDING', 'APPROVED', 'DENIED', 'CANCELLED'],
      timeFrames: ['PAST', 'CURRENT', 'FUTURE'],
    };
  }

  /** Count of active resources-directory list filters, for the dialog's
   *  read-only "inherited filters" summary. */
  private activeDirectoryFilterCount(): number {
    let count = this.getEnabledAdvancedFiltersCount();
    if (this.filterForm.get('primarySearch')?.value?.trim()) {
      count++;
    }
    const capacityStatuses = this.filterForm.get('capacityStatuses')?.value;
    if (capacityStatuses && capacityStatuses.length > 0) {
      count++;
    }
    return count;
  }

  /** Count of active time-off-requests list filters (status and time frame are
   *  configured in the dialog itself, so they're excluded here). */
  private activeRequestsFilterCount(): number {
    let count = 0;
    if (this.requestsSearchQuery?.trim()) {
      count++;
    }
    if (this.requestsOfficeFilter.length > 0) {
      count++;
    }
    if (this.requestsPracticeAreaFilter.length > 0) {
      count++;
    }
    return count;
  }

  /** Toggle compact row spacing on the resources and time-off-requests tables. */
  toggleCondensedRows(): void {
    if (this.viewMode === 'calendar') {
      return;
    }
    this.isCondensed = !this.isCondensed;
    this.userCookieService.setFilterValue(
      this.SETTINGS_PAGE_KEY,
      'condensedRows',
      this.isCondensed ? 'true' : 'false',
    );
  }

  /** Recompute the rendered columns from the column order minus hidden ones. */
  private updateDisplayedColumns(): void {
    const columns = this.columnOrder.filter(
      (column) => !this.hiddenColumns.includes(column),
    );
    if (this.showOpenInDialogColumn) {
      columns.push('openInDialog');
    }
    this.displayedColumns = columns;
  }

  /**
   * Merge a saved column order with the current default order: drop stale
   * keys and insert columns added since the preference was saved after their
   * nearest default-order predecessor.
   */
  private reconcileColumnOrder(saved: string[]): string[] {
    const result = saved.filter((column) =>
      this.DEFAULT_COLUMN_ORDER.includes(column),
    );
    this.DEFAULT_COLUMN_ORDER.forEach((column, defaultIndex) => {
      if (result.includes(column)) {
        return;
      }
      let insertAt = 0;
      for (let i = defaultIndex - 1; i >= 0; i--) {
        const predecessorIndex = result.indexOf(this.DEFAULT_COLUMN_ORDER[i]);
        if (predecessorIndex !== -1) {
          insertAt = predecessorIndex + 1;
          break;
        }
      }
      result.splice(insertAt, 0, column);
    });
    return result;
  }

  /** Columns menu — stop event propagation so the document-level backdrop
   *  doesn't immediately re-close the menu we're trying to open. */
  toggleColumnsMenu(event: Event): void {
    event.stopPropagation();
    if (this.viewMode !== 'table') {
      return;
    }
    this.showColumnsMenu = !this.showColumnsMenu;
  }

  getColumnLabel(key: string): string {
    return this.COLUMN_LABELS[key] ?? key;
  }

  /** `fullName` is always shown so rows keep their identity. */
  isColumnHideable(key: string): boolean {
    return key !== 'fullName';
  }

  isColumnVisible(key: string): boolean {
    return !this.hiddenColumns.includes(key);
  }

  /** Show/hide a column and persist the preference in the settings cookie. */
  toggleColumn(key: string): void {
    if (!this.isColumnHideable(key)) {
      return;
    }
    if (this.hiddenColumns.includes(key)) {
      this.hiddenColumns = this.hiddenColumns.filter((c) => c !== key);
    } else {
      this.hiddenColumns = [...this.hiddenColumns, key];
    }
    this.userCookieService.setFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'hiddenColumns',
      this.hiddenColumns,
    );
    this.updateDisplayedColumns();
  }

  /** Reorder columns from a Columns-menu drag and persist the new order. */
  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);
    this.userCookieService.setFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'columnOrder',
      this.columnOrder,
    );
    this.updateDisplayedColumns();
  }

  /** Restore the default column order and visibility. */
  resetColumns(): void {
    this.columnOrder = [...this.DEFAULT_COLUMN_ORDER];
    this.hiddenColumns = [...this.DEFAULT_HIDDEN_COLUMNS];
    this.userCookieService.setFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'columnOrder',
      this.columnOrder,
    );
    this.userCookieService.setFilterSelection(
      this.SETTINGS_PAGE_KEY,
      'hiddenColumns',
      this.hiddenColumns,
    );
    this.updateDisplayedColumns();
  }

  isColumnConfigDefault(): boolean {
    const sameOrder =
      this.columnOrder.join() === this.DEFAULT_COLUMN_ORDER.join();
    const sameHidden =
      [...this.hiddenColumns].sort().join() ===
      [...this.DEFAULT_HIDDEN_COLUMNS].sort().join();
    return sameOrder && sameHidden;
  }

  /** Builds the search filter the export should use, matching whatever list the user
   *  is currently looking at. The Time Off Requests view runs off a separate filter
   *  state (mirrors loadTimeOffRequests); every other view uses currentRequest. */
  private buildExportTimeOffRequest(): ResourceTimeOffRequest {
    if (this.viewMode === 'requests') {
      const searchTerm = this.requestsSearchQuery?.trim() || undefined;
      // page/pageSize are required by the ResourceListRequest interface but the
      // export endpoint returns all matching entries regardless.
      return {
        page: 1,
        pageSize: 1000,
        search: searchTerm,
        statusCodes: this.requestsStatusFilter,
        practiceAreaCodes:
          this.requestsPracticeAreaFilter.length > 0
            ? this.requestsPracticeAreaFilter
            : undefined,
        offices:
          this.requestsOfficeFilter.length > 0
            ? this.requestsOfficeFilter
            : undefined,
        timeFrames:
          this.requestsTimeFrameFilter.length > 0
            ? this.requestsTimeFrameFilter
            : undefined,
        referenceDate: this.localTodayString(),
      };
    }
    return { ...this.currentRequest };
  }

  /** Formats a YYYY-MM-DD date as MM/DD/YYYY for the export's hour-column headers. */
  private formatTimespanDate(isoDate: string): string {
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
  }

  /** Runs the export with the dialog's configuration layered over the page's
   *  inherited list filters. The billing window and time-off filters come from
   *  the dialog (ephemeral — the page's own selections are left untouched);
   *  only the chosen layout is remembered for next time, as the old menu did.
   *  Returns an observable that completes once the download has been delivered
   *  (errors if it fails) so the dialog can drive its spinner and close on
   *  success. */
  private runExportWithConfig(
    config: ExportConfigDialogResult,
  ): Observable<void> {
    this.lastExportType = config.layout;
    this.userCookieService.setFilterValue(
      this.SETTINGS_PAGE_KEY,
      'exportType',
      config.layout,
    );

    if (this.viewMode !== 'requests') {
      this.updateRequest();
    }
    const request = this.buildExportTimeOffRequest();

    // The dialog owns which time-off entries are included; override whatever the
    // page-derived request carried.
    request.statusCodes =
      config.statusCodes.length > 0 ? config.statusCodes : undefined;
    request.timeFrames =
      config.timeFrames.length > 0 ? config.timeFrames : undefined;
    request.referenceDate = this.localTodayString();

    const { startDate, endDate } = this.billingRangeFromConfig(config);

    // Shown in parentheses in the export's hour-column headers so readers
    // know which window the hours cover.
    const billingTimespanLabel = this.billingLabelFromConfig(
      config,
      startDate,
      endDate,
    );

    const onlyResourcesWithTimeOff = this.viewMode === 'requests';

    return this.executeExport(
      request,
      startDate,
      endDate,
      billingTimespanLabel,
      config.layout,
      onlyResourcesWithTimeOff,
    );
  }

  /** Billing window (yyyy-MM-dd) from the dialog's chosen period — independent
   *  of the page's selectedDateRange so exporting can't leak another tab's window. */
  private billingRangeFromConfig(config: ExportConfigDialogResult): {
    startDate: string;
    endDate: string;
  } {
    const option =
      this.dateRangeOptions.find((opt) => opt.key === config.dateRangeKey) ??
      DEFAULT_DATE_RANGE_OPTION;
    return computeDateRange(option, {
      startDate: config.customStartDate,
      endDate: config.customEndDate,
    });
  }

  /** Human-readable label for the dialog's billing period (preset name, or the
   *  MM/DD/YYYY range for a custom window). */
  private billingLabelFromConfig(
    config: ExportConfigDialogResult,
    startDate: string,
    endDate: string,
  ): string {
    const preset = this.dateRangeOptions.find(
      (option) => option.key === config.dateRangeKey,
    );
    if (preset && preset.kind !== 'custom') {
      return preset.label;
    }
    return `${this.formatTimespanDate(startDate)} - ${this.formatTimespanDate(endDate)}`;
  }

  private executeExport(
    request: ResourceTimeOffRequest,
    startDate: string,
    endDate: string,
    billingTimespanLabel: string,
    type: ResourceTimeOffExportLayout,
    onlyResourcesWithTimeOff: boolean,
  ): Observable<void> {
    this.isExporting = true;
    this.cdr.detectChanges();

    return this.resourceService
      .exportResourceTimeOff(
        request,
        startDate,
        endDate,
        billingTimespanLabel,
        type,
        onlyResourcesWithTimeOff,
      )
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          if (!response.body) {
            // Treat an empty payload as a failure so the dialog stays open and
            // the user can widen their filters and retry.
            this.snackbarService.showError('Export returned no data');
            throw new Error('EXPORT_NO_DATA');
          }
          const filename = parseFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `resource-time-off_${new Date().toISOString().slice(0, 10)}.xlsx`,
          );
          downloadBlob(response.body, filename);
          this.snackbarService.showSuccess('Export complete');
        }),
        catchError((err) => {
          // The no-data branch already surfaced its own message; only a genuine
          // request failure needs the generic toast + console log.
          if (!(err instanceof Error && err.message === 'EXPORT_NO_DATA')) {
            console.error('Error exporting time off data:', err);
            this.snackbarService.showError('Failed to export time off data');
          }
          return throwError(() => err);
        }),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      );
  }

  /**
   * Opens the browser print dialog on the time-off calendar (PBI 6471), so the
   * result can be saved as a PDF and emailed on.
   *
   * Prints the view as it stands, including the status filter and whichever of
   * the month, week, day or list views is selected. Forcing the month view here
   * would throw away the view the user deliberately chose.
   */
  printCalendar(): void {
    const api = this.timeOffCalendar()?.getApi();
    if (!api) return;

    this.printedOn = new Date();
    openPrintDialog({
      api,
      dayMaxEvents: DEFAULT_DAY_MAX_EVENTS,
      documentTitlePrefix: 'Resources Directory - Time Off',
    });
  }

  /** Labels for the statuses currently on the calendar, for the print caption. */
  get calendarStatusLabels(): string {
    const labels: Record<string, string> = {
      APPROVED: 'Approved',
      PENDING: 'Pending',
    };
    return this.calendarStatusFilter
      .map((code) => labels[code] ?? code)
      .join(', ');
  }

  onCalendarStatusChange(statuses: string[]): void {
    this.calendarStatusFilter = statuses;
    this.refilterCalendarEvents();
    this.cdr.detectChanges();
  }

  private refilterCalendarEvents(): void {
    const filtered = this.calendarTimeOffEntries.filter((entry) => {
      const code = (entry.statusCode || 'APPROVED').toUpperCase();
      return this.calendarStatusFilter.includes(code);
    });
    const timeOffEvents = mapTimeOffToEvents(filtered);
    this.calendarOptions = {
      ...this.calendarOptions,
      events: [...timeOffEvents, ...this.calendarHolidayEvents],
    };
  }

  // ---- Time Off Requests tab ----

  loadTimeOffRequests(): void {
    if (this.requestsRequest) {
      this.requestsRequest.unsubscribe();
    }

    this.isRequestsLoading = true;

    const searchTerm = this.requestsSearchQuery?.trim() || undefined;

    const request: ResourceTimeOffRequest = {
      page: 1,
      pageSize: 1000,
      search: searchTerm,
      statusCodes: this.requestsStatusFilter,
      practiceAreaCodes:
        this.requestsPracticeAreaFilter.length > 0
          ? this.requestsPracticeAreaFilter
          : undefined,
      offices:
        this.requestsOfficeFilter.length > 0
          ? this.requestsOfficeFilter
          : undefined,
      // PAST/CURRENT/FUTURE filter, evaluated server-side against the user's
      // local "today" (empty selection = no time frame filtering).
      timeFrames:
        this.requestsTimeFrameFilter.length > 0
          ? this.requestsTimeFrameFilter
          : undefined,
      referenceDate: this.localTodayString(),
    };

    this.requestsRequest = this.resourceService
      .getFilteredResourcesTimeOff(request)
      .subscribe({
        next: (result) => {
          this.timeOffRequests = result.timeOffEntries;
          this.requestsDataSource.data = this.timeOffRequests;
          this.isRequestsLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            const requestsPaginator = this.requestsPaginator();
            if (requestsPaginator) {
              this.requestsDataSource.paginator = requestsPaginator;
            }
            // One table, so one MatSort: point the data source at it and let
            // MatTableDataSource sort off its sortChange -- nothing to subscribe to.
            const requestsSort = this.requestsHeaderSort();
            if (requestsSort) {
              this.requestsDataSource.sort = requestsSort;
            }
          });
        },
        error: (err) => {
          console.error('Error loading time off requests:', err);
          this.isRequestsLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onRequestsSearchInput(): void {
    if (this.requestsSearchDebounceTimer) {
      clearTimeout(this.requestsSearchDebounceTimer);
    }
    this.requestsSearchDebounceTimer = setTimeout(() => {
      this.loadTimeOffRequests();
    }, 300);
  }

  clearRequestsSearch(): void {
    if (this.requestsSearchQuery) {
      this.requestsSearchQuery = '';
      this.loadTimeOffRequests();
    }
  }

  resetRequestsFilters(): void {
    this.requestsSearchQuery = '';
    this.requestsStatusFilter = ['PENDING'];
    this.requestsPracticeAreaFilter = [];
    this.requestsOfficeFilter = [];
    this.requestsTimeFrameFilter = ['CURRENT', 'FUTURE'];
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'offices',
      [],
    );
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'practiceAreas',
      [],
    );
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'status',
      ['PENDING'],
    );
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'timeFrames',
      ['CURRENT', 'FUTURE'],
    );
    this.loadTimeOffRequests();
  }

  hasRequestsFiltersChanged(): boolean {
    const hasSearch = !!this.requestsSearchQuery?.trim();
    const isDefaultStatus =
      this.requestsStatusFilter.length === 1 &&
      this.requestsStatusFilter[0] === 'PENDING';
    const hasPracticeAreaFilter = this.requestsPracticeAreaFilter.length > 0;
    const hasOfficeFilter = this.requestsOfficeFilter.length > 0;
    const isDefaultTimeFrame =
      this.requestsTimeFrameFilter.length === 2 &&
      this.requestsTimeFrameFilter.includes('CURRENT') &&
      this.requestsTimeFrameFilter.includes('FUTURE');
    return (
      hasSearch ||
      !isDefaultStatus ||
      hasPracticeAreaFilter ||
      hasOfficeFilter ||
      !isDefaultTimeFrame
    );
  }

  onRequestsPracticeAreaChange(codes: string[]): void {
    this.requestsPracticeAreaFilter = codes;
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'practiceAreas',
      codes,
    );
    this.loadTimeOffRequests();
  }

  onRequestsStatusChange(statuses: string[]): void {
    this.requestsStatusFilter = statuses;
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'status',
      statuses,
    );
    this.loadTimeOffRequests();
  }

  onRequestsOfficeChange(codes: string[]): void {
    this.requestsOfficeFilter = codes;
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'offices',
      codes,
    );
    this.loadTimeOffRequests();
  }

  onRequestsTimeFrameChange(timeFrames: string[]): void {
    this.requestsTimeFrameFilter = timeFrames;
    this.userCookieService.setFilterSelection(
      PageKey.TimeOffRequests,
      'timeFrames',
      timeFrames,
    );
    this.loadTimeOffRequests();
  }

  /** Formats a Date as yyyy-MM-dd from its LOCAL components. toISOString would
   *  convert to UTC and can roll the day back/forward for non-UTC users. */
  private toLocalIsoDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** Local "today" as yyyy-MM-dd for the API's DateOnly startDate filter. */
  private localTodayString(): string {
    return this.toLocalIsoDate(new Date());
  }

  approveRequest(entry: ResourceTimeOffEntryDto, note?: string): void {
    this.timeOffService
      .updateStatus(entry.timeOffRequestId, 'APPROVED')
      .subscribe({
        next: () => {
          if (note) {
            this.timeOffService
              .saveManagerNote(entry.timeOffRequestId, note, 'APPROVED')
              .subscribe();
          }
          this.snackbarService.showSuccess(
            `Time off request for ${entry.fullName} approved.`,
          );
          this.refreshTimeOffAfterChange();
        },
        error: (err) => {
          console.error('Error approving request:', err);
          this.snackbarService.showError('Failed to approve time off request.');
        },
      });
  }

  denyRequest(entry: ResourceTimeOffEntryDto, note?: string): void {
    this.timeOffService
      .updateStatus(entry.timeOffRequestId, 'DENIED')
      .subscribe({
        next: () => {
          if (note) {
            this.timeOffService
              .saveManagerNote(entry.timeOffRequestId, note, 'DENIED')
              .subscribe();
          }
          this.snackbarService.showSuccess(
            `Time off request for ${entry.fullName} returned.`,
          );
          this.refreshTimeOffAfterChange();
        },
        error: (err) => {
          console.error('Error returning request:', err);
          this.snackbarService.showError('Failed to return time off request.');
        },
      });
  }

  /** The service picks the route from ownership and status and saves the note where one is taken. */
  cancelRequest(entry: ResourceTimeOffEntryDto, note?: string): void {
    this.timeOffService
      .cancelRequestFor(
        entry.timeOffRequestId,
        {
          isOwn: this.authService.isCurrentEmployee(entry.employeeId),
          statusCode: entry.statusCode,
        },
        note,
      )
      .subscribe({
        next: () => {
          this.snackbarService.showSuccess(
            `Time off request for ${entry.fullName} cancelled.`,
          );
          this.refreshTimeOffAfterChange();
        },
        error: (err) => {
          console.error('Error cancelling request:', err);
          this.snackbarService.showError('Failed to cancel time off request.');
        },
      });
  }

  /**
   * After a request changes, refresh every source the page reads it from. The edit picker takes
   * its "other request" days from both the requests grid and the calendar's entries, so a stale
   * calendar row would keep blocking a day the change had freed; the calendar is refetched once
   * it has loaded at all, whichever view is showing.
   */
  private refreshTimeOffAfterChange(): void {
    this.loadTimeOffRequests();
    if (this.calendarLoadedOnce) {
      this.loadCalendarTimeOff();
    }
  }

  /**
   * Days held by the employee's other pending or approved requests, so the edit picker
   * cannot double-book them. Best effort: only rows this page has loaded (grid and calendar)
   * are known here; the server remains the backstop.
   */
  private otherRequestDaysFor(entry: ResourceTimeOffEntryDto): string[] {
    const days = new Set<string>();
    for (const other of [
      ...this.timeOffRequests,
      ...this.calendarTimeOffEntries,
    ]) {
      if (other.employeeId !== entry.employeeId) continue;
      if (other.timeOffRequestId === entry.timeOffRequestId) continue;
      const code = (other.statusCode || '').toUpperCase();
      if (code !== 'APPROVED' && code !== 'PENDING') continue;
      for (const day of other.days ?? []) days.add(day);
    }
    return [...days];
  }

  openRequestDetailsDialog(entry: ResourceTimeOffEntryDto): void {
    const dialogData: TimeOffDetailsDialogData = {
      timeOffRequestId: entry.timeOffRequestId,
      employeeId: entry.employeeId,
      employeeName: entry.fullName,
      statusCode: entry.statusCode,
      canApprove: this.canApproveTimeOff,
      canEditDays: this.canEditTimeOffDays,
      apiMode: 'admin',
      isOwnRequest: this.authService.isCurrentEmployee(entry.employeeId),
      hasNotes: entry.hasNotes,
      hasManagerNotes: entry.hasManagerNotes,
      typeName: entry.typeName,
      days: entry.days,
      carryOverDays: entry.carryOverDays,
      otherRequestDays: this.otherRequestDaysFor(entry),
      holidays: this.calendarHolidays,
      startDate: entry.startDate,
      endDate: entry.endDate,
      daysOff: entry.daysOff,
    };

    const dialogRef = this.dialog.open(TimeOffDetailsDialogComponent, {
      width: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: TimeOffDetailsDialogResult | null) => {
        if (!result) return;
        if (result.action === 'approve') {
          this.approveRequest(entry, result.note);
        } else if (result.action === 'return') {
          this.denyRequest(entry, result.note);
        } else if (result.action === 'cancel') {
          this.cancelRequest(entry, result.note);
        } else if (result.action === 'edit') {
          // The dialog saved the days itself; report it and refresh, as the other actions do.
          this.snackbarService.showSuccess(
            `Time off request for ${entry.fullName} updated.`,
          );
          this.refreshTimeOffAfterChange();
        }
      });
  }
}
